'use client'

import { createSerializer, parseAsBoolean, parseAsString } from 'nuqs'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { getSafeCallbackUrl } from '@/util/get-safe-callback-url'

export const signInPhoneSearchParams = {
  phone: parseAsString,
  codeSent: parseAsBoolean,
  callbackUrl: parseAsString,
}

export const signInPhoneUrl = createSerializer(signInPhoneSearchParams)

export const signInEmailSearchParams = {
  callbackUrl: parseAsString,
}

export const signInEmailUrl = createSerializer(signInEmailSearchParams)

export type MagicLinkRequestResult = {
  message?: string
  accountFound?: boolean
}

export async function signInWithMagicLinkToken(
  token: string,
  callbackUrl = '/',
): Promise<{ ok: boolean; error?: string }> {
  try {
    const result = await signIn('magic-link', {
      magicLinkToken: token,
      callbackUrl,
      redirect: false,
    })
    if (result?.ok) {
      return { ok: true }
    }
    return { ok: false, error: result?.error ?? 'Unable to sign in.' }
  } catch {
    return { ok: false, error: 'Unable to sign in.' }
  }
}

export async function requestMagicLinkCode(
  identifier: string,
  options?: { idempotencyKey?: string; callbackUrl?: string },
): Promise<MagicLinkRequestResult> {
  const res = await fetch('/api/auth/magic-link/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      identifier: identifier.trim(),
      idempotencyKey: options?.idempotencyKey ?? crypto.randomUUID(),
      ...(options?.callbackUrl ? { callbackUrl: options.callbackUrl } : {}),
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Something went wrong. Please try again.')
  }
  return {
    message: typeof data.message === 'string' ? data.message : undefined,
    accountFound: typeof data.accountFound === 'boolean' ? data.accountFound : undefined,
  }
}

export function useRedirectToPhoneSignIn() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const redirect = useCallback(
    async (phoneNumber: string) => {
      const identifier = phoneNumber.trim()
      if (!identifier) return

      setLoading(true)
      setError(null)
      try {
        const result = await requestMagicLinkCode(identifier)
        if (result.accountFound === false) {
          setError(result.message ?? 'We could not find an account for this phone number.')
          return
        }
        const rawCallbackUrl =
          typeof window !== 'undefined'
            ? (new URLSearchParams(window.location.search).get('callbackUrl') ??
              new URLSearchParams(window.location.search).get('callbackURL') ??
              undefined)
            : undefined
        const callbackUrl = getSafeCallbackUrl(rawCallbackUrl)
        router.push(
          signInPhoneUrl('/signin', {
            phone: identifier,
            codeSent: true,
            ...(callbackUrl !== '/' ? { callbackUrl } : {}),
          }),
        )
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [router],
  )

  return { redirect, loading, error, clearError: () => setError(null) }
}
